// Small shared pieces for the production screens (other UI redesign, W21–W23):
// blocker labels, the risk label, and the one jobs query. Status colour always
// backs up a WORD — never colour alone.
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  BLOCKER_COPY,
  RISK_COPY,
  URGENT_BLOCKERS,
  type JobBlocker,
  type JobRisk,
  type ProductionJobsResponse,
} from "@/lib/production";

export function useProductionJobs(scope: "open" | "all" = "open") {
  return useQuery({
    queryKey: ["production", "jobs", scope],
    queryFn: () => api<ProductionJobsResponse>(`/production/jobs?scope=${scope}&limit=500`),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}

export function BlockerTag({ blocker }: { blocker: JobBlocker }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded px-2 py-1 text-[11px] font-medium",
        URGENT_BLOCKERS.has(blocker) ? "bg-warn-bg text-warn" : "bg-[#f1f1ee] text-ink",
      )}
    >
      {BLOCKER_COPY[blocker].label}
    </span>
  );
}

export function BlockerTags({ blockers, max = 3 }: { blockers: JobBlocker[]; max?: number }) {
  if (blockers.length === 0) return <span className="text-muted" aria-label="Nothing blocking">—</span>;
  const shown = blockers.slice(0, max);
  return (
    <span className="flex flex-wrap gap-1.5">
      {shown.map((b) => (
        <BlockerTag key={b} blocker={b} />
      ))}
      {blockers.length > shown.length && (
        <span className="self-center text-[11px] text-muted">+{blockers.length - shown.length}</span>
      )}
    </span>
  );
}

export function RiskLabel({ risk }: { risk: JobRisk }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded px-2 py-1 text-[11px] font-medium",
        risk === "late" ? "bg-risk-bg text-risk" : risk === "at_risk" ? "bg-warn-bg text-warn" : risk === "on_track" ? "bg-ok-bg text-ok" : "bg-[#f1f1ee] text-ink",
      )}
    >
      {RISK_COPY[risk]}
    </span>
  );
}

/** Honest freshness line under a production screen. */
/** "2026-10-12" → "10/12/2026" — the US format (Stephanie 2026-10-12). */
const usDate = (d: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : d;
};

export function FreshnessNote({ asOf, updatedAt, truncated }: { asOf?: string; updatedAt: number; truncated?: boolean }) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <p className="mt-6 text-xs text-muted">
      {updatedAt > 0 ? `Updated ${new Date(updatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Not loaded yet"}
      {asOf ? ` · late and due dates judged as of ${usDate(asOf)}` : ""} · times in {tz}
      {truncated ? " · showing the newest 500 purchase orders" : ""}
    </p>
  );
}
